import { useState, useCallback } from 'react';
import type { TemplateConfig, UploadedAsset, AssetKey, HeadlineSegment } from '../types';
import type { TextFields } from '../components/AssetManager/TextInputs';

const STORAGE_KEY = 'buzz24_saved_projects';
const MAX_PROJECTS = 20;
// Max size (bytes) for inlining an image asset as base64 in localStorage.
// Assets larger than this are flagged needsReupload so we don't blow the quota.
const MAX_INLINE_IMAGE_BYTES = 600 * 1024; // 600 KB

// ─── Types ───────────────────────────────────────────────────

export interface SavedAsset extends Omit<UploadedAsset, 'url'> {
  url: string;
  /** True when the original was a blob:// URL we couldn't serialise (video/audio). */
  needsReupload?: boolean;
}

export interface SavedProject {
  id: string;
  name: string;
  savedAt: number;
  templateConfig: TemplateConfig;
  textFields: TextFields;
  headlineSegments: HeadlineSegment[];
  subheadlineSegments: HeadlineSegment[];
  assets: Record<string, SavedAsset>;
}

// ─── Helpers ─────────────────────────────────────────────────

function loadFromStorage(): SavedProject[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedProject[]) : [];
  } catch {
    return [];
  }
}

function persist(projects: SavedProject[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  } catch (e) {
    // quota exceeded — try without inline asset data
    const stripped = projects.map((p) => ({
      ...p,
      assets: Object.fromEntries(
        Object.entries(p.assets).map(([k, a]) => [
          k,
          { ...a, url: a.url.startsWith('data:') ? '' : a.url, needsReupload: a.url.startsWith('data:') ? true : a.needsReupload },
        ])
      ),
    }));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stripped));
    } catch {
      // still failing — nothing we can do
    }
  }
}

/**
 * Converts a blob:// URL to a base64 data URL.
 * Returns null on failure (e.g., blob expired).
 */
async function blobUrlToDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Serialise an UploadedAsset for storage:
 * - Server URLs (http/https): stored as-is — they persist as long as the server is up.
 * - Blob image URLs under the size limit: converted to base64 data URL.
 * - Blob video/audio URLs (too large): cleared, flagged needsReupload.
 */
async function serialiseAsset(asset: UploadedAsset): Promise<SavedAsset> {
  const { url, type, size } = asset;

  // Server-hosted — save the URL directly
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return { ...asset, needsReupload: false };
  }

  // Blob URL
  if (url.startsWith('blob:')) {
    if (type === 'image' && size <= MAX_INLINE_IMAGE_BYTES) {
      const dataUrl = await blobUrlToDataUrl(url);
      if (dataUrl) return { ...asset, url: dataUrl, needsReupload: false };
    }
    // Video / audio or oversized image — can't inline; mark for re-upload
    return { ...asset, url: '', needsReupload: true };
  }

  return { ...asset, needsReupload: false };
}

// ─── Hook ────────────────────────────────────────────────────

export function useSavedProjects() {
  const [projects, setProjects] = useState<SavedProject[]>(loadFromStorage);

  /**
   * Save the current project state.
   * Serialises assets asynchronously (blob → base64 where possible).
   */
  const saveProject = useCallback(
    async (
      templateConfig: TemplateConfig,
      textFields: TextFields,
      headlineSegments: HeadlineSegment[],
      subheadlineSegments: HeadlineSegment[],
      assets: Partial<Record<AssetKey, UploadedAsset>>,
      name?: string
    ): Promise<string> => {
      // Serialise all assets (async)
      const serialised: Record<string, SavedAsset> = {};
      await Promise.all(
        Object.entries(assets).map(async ([key, asset]) => {
          if (asset) serialised[key] = await serialiseAsset(asset);
        })
      );

      const id = `proj_${Date.now()}`;
      const entry: SavedProject = {
        id,
        name: name?.trim() || `${templateConfig.metadata.name} — ${new Date().toLocaleDateString()}`,
        savedAt: Date.now(),
        templateConfig,
        textFields,
        headlineSegments: [...headlineSegments],
        subheadlineSegments: [...subheadlineSegments],
        assets: serialised,
      };

      setProjects((prev) => {
        const next = [entry, ...prev].slice(0, MAX_PROJECTS);
        persist(next);
        return next;
      });

      return id;
    },
    []
  );

  const deleteProject = useCallback((id: string): void => {
    setProjects((prev) => {
      const next = prev.filter((p) => p.id !== id);
      persist(next);
      return next;
    });
  }, []);

  const renameProject = useCallback((id: string, name: string): void => {
    setProjects((prev) => {
      const next = prev.map((p) =>
        p.id === id ? { ...p, name: name.trim() || p.name } : p
      );
      persist(next);
      return next;
    });
  }, []);

  return { projects, saveProject, deleteProject, renameProject };
}
